import React from 'react';
import SEO from '../components/SEO';
import ImageSlot from '../components/ImageSlot';
import { SystemStory } from '../components/VisualStories';
import { Brain, Database, DeviceMobile, TerminalWindow, Cloud, ShieldCheck } from '@phosphor-icons/react';
import styles from '../styles/pages/Technology.module.css';

const layerIcons = [Brain, Database, DeviceMobile, TerminalWindow, Cloud, ShieldCheck];

const toolGroups = [
  {
    label: 'Intelligence',
    summary: 'Models, agents, and evaluation.',
    tools: [
      { name: 'Anthropic', logo: '/images/logos/anthropic.svg' },
      { name: 'OpenAI', logo: '/images/logos/openai.svg' },
      { name: 'Gemini', logo: '/images/logos/gemini.svg' },
      { name: 'LangChain', logo: '/images/logos/langchain.svg' },
      { name: 'MCP', logo: '/images/logos/mcp.svg' },
      { name: 'Cursor', logo: '/images/logos/cursor.svg' },
    ],
  },
  {
    label: 'Data & Cloud',
    summary: 'Governed data for reporting and automation.',
    tools: [
      { name: 'Databricks', logo: '/images/logos/databricks.svg' },
      { name: 'Snowflake', logo: '/images/logos/snowflake.svg' },
      { name: 'PostgreSQL', logo: '/images/logos/postgresql.svg' },
      { name: 'SQL', logo: '/images/logos/sql.svg' },
      { name: 'dbt', logo: '/images/logos/dbt.svg' },
      { name: 'Google Cloud', logo: '/images/logos/googlecloud.svg' },
    ],
  },
  {
    label: 'Product & Delivery',
    summary: 'Interfaces and services your team can own.',
    tools: [
      { name: 'React', logo: '/images/logos/react.svg' },
      { name: 'TypeScript', logo: '/images/logos/typescript.svg' },
      { name: 'Python', logo: '/images/logos/python.svg' },
      { name: 'FastAPI', logo: '/images/logos/fastapi.svg' },
      { name: 'Docker', logo: '/images/logos/docker.svg' },
      { name: 'GitHub', logo: '/images/logos/github.svg' },
    ],
  },
];

const stack = [
  {
    title: 'Multi-Agent Systems & MCP',
    outcome: 'Give useful work clear boundaries.',
    detail: 'Claude · GPT · Gemini · Model Context Protocol (MCP) · custom multi-agent graphs · bounded memory & tool execution · automated security analysis harnesses.',
  },
  {
    title: 'Enterprise Data & Lakehouse',
    outcome: 'Make trusted information available where it matters.',
    detail: 'Databricks · Snowflake · dbt · Delta Lake · PostgreSQL & pgvector · SQLite embedded with offline-first synchronization · MDM & CDC.',
  },
  {
    title: 'Client & Mobile Engineering',
    outcome: 'Put the capability in people’s hands.',
    detail: 'Flutter for native iOS & Android (reactive state, custom animation) · React · TypeScript · Vite · WebGL / GLSL shaders.',
  },
  {
    title: 'Systems & Developer Tooling',
    outcome: 'Make the build repeatable for the next team.',
    detail: 'Cross-platform CLI systems diagnostics (Sprout) · environment auto-repair · PATH verification · Docker containerization · GitHub Actions CI/CD.',
  },
  {
    title: 'Backend Services & APIs',
    outcome: 'Connect new capabilities to existing work.',
    detail: 'Python (FastAPI) · Node.js (Express) · Google Cloud Run · Supabase · authenticated RESTful APIs · usage metering & webhooks.',
  },
  {
    title: 'Governance & Operational SLA',
    outcome: 'Keep systems visible and accountable.',
    detail: 'ASQ Certified Six Sigma Black Belt methodology · Monitored delivery schedules · RBAC · prompt audit trails · production observability.',
  },
];

const liveProofs = [
  {
    title: 'Sprout Developer CLI',
    desc: 'An open-source diagnostic and installer CLI built by AURABLOX that inspects local developer machines, plans tool installations, and verifies PATH and binary health.',
    status: 'AURABLOX product',
    scope: 'Open-source CLI · MIT',
    cta: 'Inspect demo & source ↗',
    href: 'https://murderszn.github.io/sprout/',
    imageWebp: '/images/projects/sprout-website-hero.webp',
    imagePng: '/images/projects/sprout-website-hero.png',
  },
  {
    title: 'Cerberus Security Watchdog',
    desc: 'A browser-based security analysis harness for inspecting repository dependencies, configuration posture, and exposed secrets.',
    status: 'Proof of concept',
    scope: 'Browser security scanner',
    cta: 'Open proof of concept ↗',
    href: 'https://cerberus-github-auth.jjohnso75.workers.dev/',
    imageWebp: '/images/projects/cerberus-preview.webp',
    imagePng: '/images/projects/cerberus-preview.png',
  },
  {
    title: 'Nameplate Property CMMS',
    desc: 'An interactive application prototype for property equipment records, preventative maintenance workflows, and offline-first field access.',
    status: 'Product prototype',
    scope: 'Interactive web app',
    cta: 'Open product prototype ↗',
    href: 'https://nameplate-3dq.pages.dev/',
    imageWebp: '/images/projects/nameplate-preview.webp',
    imagePng: '/images/projects/nameplate-preview.png',
  },
  {
    title: 'Gym Log (NotSquat)',
    desc: 'An internal consumer application pairing on-device pose estimation and movement tracking with a structured workout log.',
    status: 'Internal product',
    scope: 'On-device vision app',
    cta: 'Open live web app ↗',
    href: 'https://notsquat.org/',
    imageWebp: '/images/projects/gymlog-preview.webp',
    imagePng: '/images/projects/gymlog-preview.png',
  },
];

const Technology = () => (
  <div className={`page technology-page ${styles.page}`}>
    <SEO
      title="Our Work & Engineering Stack"
      description="Inspect live AURABLOX products, working prototypes, and the engineering stack we use to build software, data systems, and applied AI."
      path="/technology"
    />

    <header className="page-head page-head--media" id="top">
      <div className="page-head__text">
        <p className={styles.eyebrow}>Public developer tools · Independent product examples</p>
        <h1 className="kinetic-title">Built to work together.</h1>
        <p className="page-head__lede">
          Our consulting focus is commercial data architecture. These public developer tools and independent product experiments show other engineering work by the team; they are separate from the 90-day data foundation engagement.
        </p>
      </div>
      <ImageSlot
        src="/images/aurablox-systems.jpg"
        alt="AURABLOX tech stack visualization"
        ratio="4/3"
        label="Technology atmospheric — constructivist systems architecture"
      />
    </header>

    <main>
      <section className="section" aria-labelledby="proofs">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 01 // PUBLIC WORK</span>
          <span className="section-index-bar__right">PRODUCTS · PROTOTYPES · PROOFS OF CONCEPT</span>
        </div>
        <div className="section__header">
          <h2 id="proofs">Our work</h2>
          <p>
            Public tools and independent product examples you can inspect directly. These are engineering examples, not case studies of results from the commercial data foundation offer.
          </p>
        </div>
        <div className="technology-proofs">
          {liveProofs.map(({ title, desc, status, scope, cta, href, imageWebp, imagePng }, i) => (
            <article key={title} className="technology-proof">
              <a
                className="technology-proof__banner"
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={`Visit ${title} live platform`}
              >
                <picture>
                  <source srcSet={imageWebp} type="image/webp" />
                  <img src={imagePng} alt={`${title} live platform preview`} loading="lazy" />
                </picture>
              </a>
              <div className="technology-proof__body">
                <div className="technology-proof__meta">
                  <span>{String(i + 1).padStart(2, '0')} / {String(liveProofs.length).padStart(2, '0')}</span>

                </div>
                <p className={styles.proofStatus}>{status} · {scope}</p>
                <h3>{title}</h3>
                <p>{desc}</p>
                <a href={href} target="_blank" rel="noreferrer">{cta}</a>
              </div>
            </article>
          ))}
        </div>
        <div className="technology-proofs__more">
          <a
            href="https://resume-jj.web.app#skills"
            target="_blank"
            rel="noreferrer"
            className="card__link"
          >
            Review full technical skills matrix on Josh's resume ↗
          </a>
        </div>
      </section>

      <section className={`section ${styles.walkthrough}`} id="sprout-demo" aria-labelledby="sprout-walkthrough">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 02 // PRODUCT WALKTHROUGH</span>
          <span className="section-index-bar__right">REQUEST → REVIEW → VERIFY</span>
        </div>
        <div className="section__header">
          <p className={styles.eyebrow}>PRODUCT DEMONSTRATION · SPROUT DEVELOPER CLI</p>
          <h2 id="sprout-walkthrough">How Sprout plans a tool install</h2>
          <p>
            A source-backed walkthrough of the GitHub CLI recipe. Sprout must be installed first; completing an install requires a supported package manager, network access, and permission to change the host machine.
          </p>
        </div>
        <div className={styles.walkthroughGrid}>
          <article className={styles.walkthroughStep}>
            <span className={styles.stepNumber}>01 / INPUT</span>
            <h3>Request a tool</h3>
            <pre><code>{`$ sprout install gh`}</code></pre>
            <p>The developer requests GitHub CLI. Before planning, Sprout inspects the local OS, CPU architecture, active shell, RC profile, PATH, and installed package managers.</p>
          </article>
          <article className={styles.walkthroughStep}>
            <span className={styles.stepNumber}>02 / REVIEW</span>
            <h3>Inspect plan &amp; confirm</h3>
            <p>The plan shows the detected environment, the package-manager command, and a confirmation prompt. The proposed command depends on the host.</p>
            <p>Sprout matches a curated recipe for GitHub CLI, prints each proposed command array, and pauses for explicit confirmation before mutating the system.</p>
          </article>
          <article className={styles.walkthroughStep}>
            <span className={styles.stepNumber}>03 / VERIFICATION</span>
            <h3>Verify the installation</h3>
            <pre><code>gh --version</code></pre>
            <p>After the package manager finishes, Sprout runs the recipe’s verification check and reports the real exit status and binary output.</p>
          </article>
        </div>
        <div className={styles.walkthroughNotes}>
          <div>
            <span className={styles.stepNumber}>WHAT WE BUILT</span>
            <p>An open-source TypeScript CLI (<code>sprout-install</code>) that inspects local developer machines, diagnoses PATH and shell configuration drift, and runs curated tool installations with step-by-step confirmation.</p>
          </div>
          <div>
            <span className={styles.stepNumber}>ENGINEERING CHALLENGE</span>
            <p>Keeping command execution reviewable across supported environments. Rather than passing model output into a shell string (<code>sh -c</code>), Sprout validates structured plans, executes commands as argument arrays, and requires confirmation before state-changing steps.</p>
          </div>
          <div>
            <span className={styles.stepNumber}>LIMITATION</span>
            <p>Sprout is scoped to local developer-tool installs, configuration, and PATH diagnostics. It is not a general coding agent, and installs still depend on the host machine’s network, permissions, and package sources.</p>
          </div>
        </div>
        <div className={styles.sourceLinks}>
          <a href="https://github.com/murderszn/sprout/blob/main/README.md#how-a-run-works" target="_blank" rel="noreferrer">Read the run flow ↗</a>
          <a href="https://github.com/murderszn/sprout/blob/main/src/cli.ts" target="_blank" rel="noreferrer">Inspect CLI source ↗</a>
          <a href="https://murderszn.github.io/sprout/" target="_blank" rel="noreferrer">Open interactive Sprout demo ↗</a>
        </div>
      </section>

      <section className="visual-section" aria-labelledby="system-diagram-heading">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 03 // SYSTEM TOPOLOGY</span>
          <span className="section-index-bar__right">DATA → INTELLIGENCE → PRODUCT</span>
        </div>
        <div className="visual-section__copy">
          <h2 id="system-diagram-heading">How data, models, and interfaces connect</h2>
          <p>Data contracts, bounded model execution, and product interfaces operating as one system.</p>
        </div>
        <SystemStory kind="architecture" />
      </section>

      <section className="section" aria-labelledby="stack">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 04 // ARCHITECTURAL LAYERS</span>
          <span className="section-index-bar__right">SIX SYSTEM BOUNDARIES</span>
        </div>
        <div className="section__header">
          <h2 id="stack">Architectural layers</h2>
          <p>Each layer has a defined responsibility and boundary in the stack.</p>
        </div>
        <div className="architecture-grid">
          {stack.map(({ title, outcome, detail }, i) => {
            const Icon = layerIcons[i];
            return (
              <article key={title} className="architecture-card">
                <div className="architecture-card__mark">
                  <Icon size={24} weight="regular" aria-hidden="true" />
                </div>
                <div>
                  <h3>{title}</h3>
                  <p className="architecture-card__outcome">{outcome}</p>
                  <details className="reading-details"><summary>Inside this layer</summary><p className="architecture-card__detail">{detail}</p></details>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="technology-tools" aria-labelledby="tooling-heading">
        <div className="section-index-bar" aria-hidden="true">
          <span className="section-index-bar__left"><span className="seal seal--sm" /> 05 // WORKING STACK</span>
          <span className="section-index-bar__right">MODELS · DATA · PRODUCT</span>
        </div>
        <div className="technology-tools__intro">
          <p className="technology-tools__eyebrow">AURABLOX / THE WORKING STACK</p>
          <h2 id="tooling-heading">Core engineering stack</h2>
          <p>We select and connect tools around the system your team needs to run.</p>
        </div>
        <div className="technology-tools__groups">
          {toolGroups.map(({ label, summary, tools }, i) => (
            <article key={label} className="technology-tool-group">
              <div className="technology-tool-group__head">
                <span>{String(i + 1).padStart(2, '0')}</span>
                <h3>{label}</h3>
              </div>
              <p>{summary}</p>
              <div className="technology-tool-group__tools">
                {tools.map(({ name, logo }) => (
                  <span className="technology-tool" key={name}>
                    {logo ? (
                      <img src={logo} alt="" aria-hidden="true" />
                    ) : (
                      <span className="technology-tool__monogram" aria-hidden="true">{name.slice(0, 2).toUpperCase()}</span>
                    )}
                    <span>{name}</span>
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

    </main>
  </div>
);

export default Technology;
